import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';
import { tokenManager } from './tokenManager';
import { silentRefresh } from '../../features/auth/api/auth';

interface ErrorResponse {
  message: string;
  [key: string]: unknown;
}

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// API version - change this when migrating to a new API version
const API_VERSION = 'v1';

// Build base URL with version
const getBaseURL = () => {
  const baseUrl = import.meta.env.VITE_BACKEND_API_URL || 'https://dev.dalaride.co.tz/api';
  return `${baseUrl}/${API_VERSION}`;
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  withCredentials: true, // Enable cookies
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  config => {
    const token = tokenManager.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError<ErrorResponse>) => {
    const originalRequest = error.config;

    // Handle 401 errors
    if (error.response?.status === 401 && originalRequest) {
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      // Don't retry refresh requests themselves — just reject.
      // AuthContext.initAuth() handles refresh failures gracefully.
      if (originalRequest.url?.includes('/auth/token/refresh')) {
        tokenManager.clearTokens();
        return Promise.reject(error);
      }

      // Skip refresh attempts if we're already on the login page
      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      isRefreshing = true;

      try {
        console.log('🔄 Axios interceptor: Starting token refresh...');
        const refreshResponse = await silentRefresh();

        if (refreshResponse && refreshResponse.access_token) {
          console.log('✅ Axios interceptor: Token refresh successful');
          tokenManager.updateToken(
            refreshResponse.access_token,
            refreshResponse.expires_in
          );

          processQueue(null, refreshResponse.access_token);

          originalRequest.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
          return axiosInstance(originalRequest);
        } else {
          console.log('❌ Axios interceptor: Token refresh failed');
          processQueue(new Error('Refresh failed'));
          tokenManager.clearTokens();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.log('❌ Axios interceptor: Token refresh error:', refreshError);
        processQueue(refreshError);
        tokenManager.clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // List of endpoints that should fail silently (no toast)
    const silentEndpoints = [
      '/auth/token/refresh',
      '/auth/google',
      '/auth/me',
      '/auth/register',
      '/users/me',
    ];

    // Check if this is a silent endpoint
    const isSilentEndpoint = silentEndpoints.some(
      endpoint => originalRequest?.url?.includes(endpoint)
    );

    // Don't show toast for:
    // - Silent auth endpoints (refresh, oauth, register, etc.)
    // - 401 errors (handled above)
    // - Network errors during auth checks
    // - Validation errors (400) - let the form handle them
    const shouldShowToast = 
      !isSilentEndpoint && 
      error.response?.status !== 401 &&
      error.response?.status !== 400 &&
      !(originalRequest?.url?.includes('/auth/'));

    if (shouldShowToast) {
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'An error occurred';
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

// API client methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance
      .get(url, config)
      .then((response: AxiosResponse<T>) => response.data),

  post: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> =>
    axiosInstance
      .post(url, data, config)
      .then((response: AxiosResponse<T>) => response.data),

  put: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> =>
    axiosInstance
      .put(url, data, config)
      .then((response: AxiosResponse<T>) => response.data),

  patch: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> =>
    axiosInstance
      .patch(url, data, config)
      .then((response: AxiosResponse<T>) => response.data),

  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance
      .delete(url, config)
      .then((response: AxiosResponse<T>) => response.data),
};
